import { useMemo } from "react";

import { DateTime } from "luxon";
import {
  CameraIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CubeTransparentIcon,
  CurrencyPoundIcon,
  InboxArrowDownIcon,
  MapPinIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  TagIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import ContactFromId from "../../contacts/ContactFromId";
import classNames from "../../../utils/classNames";
import formatDateTime from "../../../utils/formatDateTime";
import { IPart } from "../../../models/Part";

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface TimelineEvent {
  id: string;
  label: string;
  date: string | null | undefined;
  actorId?: string | null;
  icon: IconType;
  colour: string;
}

const eventColour = (done: boolean) =>
  done
    ? "bg-primary-100 text-primary-700 ring-primary-200"
    : "bg-gray-100 text-gray-400 ring-gray-200";

export default function StockTimeline({ part }: { part: IPart }) {
  const events = useMemo<TimelineEvent[]>(() => {
    const listedDate = part.listedDate ?? part.offers?.[0]?.listingStarted;
    return [
      {
        id: "created",
        label: "Created",
        date: part.created,
        icon: TagIcon,
        colour: eventColour(!!part.created),
      },
      {
        id: "dismantled",
        label: "Dismantled",
        date: part.removedOn,
        actorId: part.removedBy,
        icon: CubeTransparentIcon,
        colour: eventColour(!!part.removedOn),
      },
      {
        id: "imaged",
        label: "Imaged",
        date: part.imagedOn,
        actorId: part.imagedBy,
        icon: CameraIcon,
        colour: eventColour(!!part.imagedOn),
      },
      {
        id: "qc",
        label: "Passed QC",
        date: part.passedQcOn,
        actorId: part.passedQcBy,
        icon: ShieldCheckIcon,
        colour: eventColour(!!part.passedQcOn),
      },
      {
        id: "located",
        label: "Located",
        date: part.locatedOn,
        actorId: part.locatedBy,
        icon: MapPinIcon,
        colour: eventColour(!!part.locatedOn),
      },
      {
        id: "priced",
        label: "Priced",
        date: part.pricedOn,
        actorId: part.pricedBy,
        icon: CurrencyPoundIcon,
        colour: eventColour(!!part.pricedOn),
      },
      {
        id: "listed",
        label: "Listed on eBay / Marketplaces",
        date: listedDate,
        icon: RocketLaunchIcon,
        colour: eventColour(!!listedDate),
      },
      {
        id: "sold",
        label: "Sold",
        date: part.soldDate,
        icon: ShoppingCartIcon,
        colour: eventColour(!!part.soldDate),
      },
      {
        id: "picked",
        label: "Picked",
        date: part.pickedOn,
        actorId: part.pickedBy,
        icon: InboxArrowDownIcon,
        colour: eventColour(!!part.pickedOn),
      },
      {
        id: "shipped",
        label: "Shipped",
        date: part.shippedOn,
        actorId: part.shippedBy,
        icon: TruckIcon,
        colour: eventColour(!!part.shippedOn),
      },
      {
        id: "delivered",
        label: "Marked as Delivered",
        date: part.deliveredOn,
        actorId: part.deliveredBy,
        icon: CheckBadgeIcon,
        colour: eventColour(!!part.deliveredOn),
      },
    ];
  }, [part]);

  const firstPendingId = useMemo(() => {
    const pending = events.find((e) => !e.date);
    return pending?.id;
  }, [events]);

  const durations = useMemo(() => {
    const byId: Record<string, { text: string; fromLabel: string } | null> = {};
    let prev: { date: DateTime; label: string } | null = null;
    for (const event of events) {
      if (!event.date) {
        byId[event.id] = null;
        continue;
      }
      const current = DateTime.fromISO(event.date);
      if (!current.isValid) {
        byId[event.id] = null;
        continue;
      }
      if (prev) {
        byId[event.id] = {
          text: formatGap(current.diff(prev.date).as("milliseconds")),
          fromLabel: prev.label,
        };
      } else {
        byId[event.id] = null;
      }
      prev = { date: current, label: event.label };
    }
    return byId;
  }, [events]);

  return (
    <Card title="Part Lifecycle" bodyClassName="p-6">
      <ol className="relative border-l border-gray-200 ml-3">
        {events.map((event, idx) => {
          const isLast = idx === events.length - 1;
          const done = !!event.date;
          const isCurrent = event.id === firstPendingId;
          const Icon = done ? CheckCircleIcon : event.icon;

          return (
            <li
              key={event.id}
              className={classNames("ml-6", isLast ? "" : "pb-6")}
            >
              <span
                className={classNames(
                  "absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white",
                  event.colour,
                  isCurrent ? "animate-pulse" : ""
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
                <div className="flex items-center gap-2">
                  <p
                    className={classNames(
                      "text-sm font-medium",
                      done ? "text-gray-900" : "text-gray-400"
                    )}
                  >
                    {event.label}
                  </p>
                  {event.actorId && (
                    <span className="text-xs text-gray-500">
                      by <ContactFromId contactId={event.actorId} />
                    </span>
                  )}
                </div>
                <p
                  className={classNames(
                    "text-xs",
                    done ? "text-gray-600" : "text-gray-400"
                  )}
                >
                  {formatDateTime(event.date, DateTime.DATETIME_SHORT, "Pending")}
                </p>
              </div>
              {durations[event.id] && (
                <p className="mt-0.5 text-[11px] text-gray-400">
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-gray-500">
                    +{durations[event.id]!.text}
                  </span>{" "}
                  <span className="text-gray-400">
                    after {durations[event.id]!.fromLabel}
                  </span>
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

function formatGap(ms: number): string {
  if (!isFinite(ms) || ms <= 0) return "same moment";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "min" : "mins"}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days} ${days === 1 ? "day" : "days"}`;
  const weeks = Math.round(days / 7);
  if (weeks < 9) return `${weeks} ${weeks === 1 ? "week" : "weeks"}`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"}`;
  const years = Math.round(days / 365);
  return `${years} ${years === 1 ? "year" : "years"}`;
}
